import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useLocation, useHistory, useParams } from 'react-router-dom';

import { ArrowBack } from '@mui/icons-material';

import { useConfig } from '../../ConfigContext';
import { EmailInput } from '../../_shared/form/EmailInput';
import { PasswordInput } from '../../_shared/form/PasswordInput';
import { CountryInput } from '../../_shared/form/CountryInput';
import { DateInput } from '../../_shared/form/DateInput';
import { GenderInput } from '../../_shared/form/GenderInput';
import { CheckboxInput } from '../../_shared/form/CheckboxInput';
import { SubmitButton } from '../../_shared/form/SubmitButton';
import { ProviderPayload, RegisterProvider, RoutePrams, SignupPayload } from '../Authentication.model';
import { useAuth } from '../AuthenticationContext';
import { NicknameInput } from '../../_shared/form/NicknameInput';

interface InitialValuesFormik {
  gender?: string;
  country?: string;
  bhvrNews?: boolean;
  nickName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  day?: string;
  month?: string;
  year?: string;
  birthDate?: string;
}
interface LocationRegister {
  email: string;
  idToken: string;
  type: string;
}

export function Register(): JSX.Element {
  const location = useLocation<LocationRegister>();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const config = useConfig();
  const auth = useAuth();
  const params = useParams<RoutePrams>();

  const [signupSchema, setSignupSchema] = useState(null);
  const [provider, setProvider] = useState<RegisterProvider | null>(null);

  const [initialValuesFormik] = useState<InitialValuesFormik>({
    gender: '',
    country: '',
    bhvrNews: false,
    nickName: '',
    email: location?.state?.email || '',
    password: '',
    confirmPassword: '',
    day: '',
    month: '',
    year: '',
    birthDate: null,
  });

  const setYupSchema = (checkPassword = true): void => {
    const fields = {
      country: Yup.string()
        .required('form.field-required'),
      email: Yup.string()
        .email('form.mail-invalid')
        .required('form.field-required'),
      month: Yup.string()
        .required('form.field-required'),
      day: Yup.string()
        .required('form.field-required'),
      year: Yup.string()
        .required('form.field-required'),
      nickName: Yup.string()
        .required('form.field-required')
        .min(2, 'form.field-error-nickname-length')
        .max(32, 'form.field-error-nickname-length')
        .matches(/^[A-Za-z].*$/, 'form.field-error-nickname-letter-start')
        .matches(/^[\w-]+$/, 'form.field-error-nickname-alphanumeric'),
      password: null as Yup.StringSchema,
      confirmPassword: null as Yup.StringSchema,
    };
    // TODO: Get translations for error messages
    if (checkPassword) {
      fields.password = Yup.string()
        .required('form.field-required')
        .matches(
          /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,33}$/,
          'form.matches-invalid',
        );
      fields.confirmPassword = Yup.string()
        .required('form.field-required')
        .oneOf([Yup.ref('password'), null], 'form.password-not-identical');
    }

    setSignupSchema(Yup.object().shape(fields));
  };

  const onBack = (): void => {
    history.push(params && params.gameId
      ? `/auth/signup/${params.gameId}` : '/auth/signup');
  };

  const setPayloadProvider = (values: InitialValuesFormik): ProviderPayload => {
    if (provider?.type) {
      return {
        provider: provider.type,
        parameters: {
          idToken: provider.idToken,
        },
      };
    }
    return {
      provider: 'password',
      parameters: {
        email: values.email,
        password: values.password,
      },
    };
  };

  const handleSubmitForm = async (
    values: InitialValuesFormik,
    { setFieldError, setSubmitting }: FormikHelpers<InitialValuesFormik>,
  ): Promise<void> => {
    const date = new Date();
    const dateBirthDate = new Date(+values.year, +values.month - 1, +values.day);
    let birthDate = '';

    if (+values.day > 31 || +values.month > 12
      || +values.year < date.getFullYear() - 100 || +values.year > date.getFullYear()) {
      setFieldError('year', 'form.date-incorrect');
      setSubmitting(false);
      return;
    }

    if (dateBirthDate.getDate() !== +values.day) {
      setFieldError('birthDate', 'form.date-incorrect');
      setSubmitting(false);
      return;
    }

    if (new Date(+values.year + 18, +values.month - 1, +values.day) > new Date()) {
      setFieldError('year', 'errors.under18Years');
      setSubmitting(false);
      return;
    }

    birthDate = `${values.year}-${values.month}-${values.day}`;

    const providerParams = setPayloadProvider(values);

    const payload: SignupPayload = {
      country: values.country,
      birthDate,
      gender: values.gender,
      bhvrNews: values.bhvrNews,
      nickName: values.nickName,
      communicationLang: i18n.language !== 'fr' ? 'en' : 'fr',
      ...providerParams,
    };

    if (config.clientConfig.recaptchaEnabled) {
      const token = await window.grecaptcha.execute(
        config.serverConfig.website.recaptcha.clientId,
        { action: 'login' },
      );
      await auth.register(payload, values.email, values.password, token);
    } else {
      await auth.register(payload, values.email, values.password);
    }

    setSubmitting(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    let providerFromLocation;
    if (location.state?.email && location.state?.idToken && location.state?.type) {
      providerFromLocation = {
        email: location.state.email,
        idToken: location.state.idToken,
        type: location.state.type,
      };
      setProvider(providerFromLocation);
    }

    setYupSchema(!providerFromLocation);
  }, []);

  return (
    <div className={params?.gameId ? `full-page-form ${params.gameId}`
      : 'full-page-form'}
    >
      <div className='header-container'>
        <span onClick={onBack} className='back-btn'>
          <ArrowBack className='icon' />
          <b>{t('button.back').toUpperCase()}</b>
        </span>
        <h2 className='header'>{t('button.create-account')}</h2>
      </div>

      <div className='form-container'>
        <Formik
          initialValues={initialValuesFormik}
          validateOnChange={false}
          validateOnBlur
          validationSchema={signupSchema}
          enableReinitialize
          onSubmit={handleSubmitForm}
        >
          {({ setFieldValue, values }) => (
            <Form className='form-light'>
              <EmailInput disabled={!!provider} />
              {(!provider || !provider.type)
                && (
                  <>
                    <PasswordInput name='password' />
                    <PasswordInput name='confirmPassword' />
                  </>
                )}
              <NicknameInput name='nickName' />
              <DateInput name='birthDate' />
              <GenderInput />
              <CountryInput
                setFieldValue={setFieldValue}
                values={values.country}
              />
              <CheckboxInput
                name='bhvrNews'
                label={
                  t('form.label.checkbox.bhvrNews')
                }
              />

              <SubmitButton name='create-account' containerClassName='my-5' />
            </Form>
          )}
        </Formik>
      </div>
      <div className='legal-register'>
        {t('auth.register.legal-register-18-1')}
        <a
          className='dbd-link font-weight-bold'
          target='_blank'
          rel='noopener noreferrer'
          href={t('auth.register.legal-register-18-link1')}
        >
          {t('auth.register.legal-register-18-2')}
        </a>
        {t('auth.register.legal-register-18-3')}
        <a
          className='dbd-link font-weight-bold'
          target='_blank'
          rel='noopener noreferrer'
          href={t('auth.register.legal-register-18-link2')}
        >
          {t('auth.register.legal-register-18-4')}
        </a>
      </div>
    </div>
  );
}
